import React from 'react';
import dayjs from 'dayjs';
import type { Store as StoreType } from '../../../../services/authService';
import type { StoreDetailsTab } from '../../../../types/features/storeDetails/page';
import {
    getStoreAuditReportsDateRange,
    sendStoreReconciliationEmail,
    type StoreAuditReportsResponse,
} from '../../../../services/storeAuditReportsService';
import { createDefaultAuditDateRange, formatAuditDateForApi, formatAuditDateRangeLabel } from '../utils/storeDetailsAuditsUtils';

type UseStoreDetailsAuditsParams = {
    isSuperAdmin: boolean;
    store: StoreType | null;
    storeId?: string;
    activeTab: StoreDetailsTab;
};

export const useStoreDetailsAudits = ({ isSuperAdmin, store, storeId, activeTab }: UseStoreDetailsAuditsParams) => {
    const [auditDateRange, setAuditDateRange] = React.useState<[Date | null, Date | null]>(createDefaultAuditDateRange());
    const [auditReports, setAuditReports] = React.useState<StoreAuditReportsResponse | null>(null);
    const [auditReportsLoading, setAuditReportsLoading] = React.useState(false);
    const [auditReportsError, setAuditReportsError] = React.useState('');
    const [reconcileDialogOpen, setReconcileDialogOpen] = React.useState(false);
    const [reconcileDate, setReconcileDate] = React.useState<Date | null>(createDefaultAuditDateRange()[1]);
    const [forceResend, setForceResend] = React.useState(false);
    const [reconciliationSending, setReconciliationSending] = React.useState(false);
    const [reconciliationResponse, setReconciliationResponse] = React.useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const dateRangeLabel = React.useMemo(() => formatAuditDateRangeLabel(auditDateRange), [auditDateRange]);

    const loadAuditReports = React.useCallback(async () => {
        const [start, end] = auditDateRange;
        if (!storeId || !start || !end) return;

        try {
            setAuditReportsLoading(true);
            setAuditReportsError('');
            const response = await getStoreAuditReportsDateRange(storeId, formatAuditDateForApi(start), formatAuditDateForApi(end));
            setAuditReports(response);
        } catch (error) {
            console.error('Error loading store audit reports:', error);
            setAuditReportsError('Unable to load audit reports. Please try again.');
        } finally {
            setAuditReportsLoading(false);
        }
    }, [auditDateRange, storeId]);

    React.useEffect(() => {
        if (auditDateRange[1]) {
            setReconcileDate(auditDateRange[1]);
        }
    }, [auditDateRange]);

    React.useEffect(() => {
        if (!isSuperAdmin || activeTab !== 'audits') return;
        void loadAuditReports();
    }, [activeTab, isSuperAdmin, loadAuditReports]);

    const openReconciliationDialog = React.useCallback(() => {
        setReconciliationResponse(null);
        setReconcileDialogOpen(true);
    }, []);

    const closeReconciliationDialog = React.useCallback(() => {
        setReconcileDialogOpen(false);
    }, []);

    const clearReconciliationResponse = React.useCallback(() => {
        setReconciliationResponse(null);
    }, []);

    const handleSendReconciliationEmail = React.useCallback(async () => {
        if (!store?.id || !reconcileDate) {
            setReconciliationResponse({ type: 'error', text: 'Store and report date are required.' });
            return;
        }

        try {
            setReconciliationSending(true);
            setReconciliationResponse(null);
            await sendStoreReconciliationEmail(store.id, dayjs(reconcileDate).format('YYYY-MM-DD'), forceResend);
            setReconciliationResponse({ type: 'success', text: 'Reconciliation email sent successfully.' });
        } catch (error) {
            console.error('Failed to send reconciliation email', error);
            setReconciliationResponse({ type: 'error', text: 'Failed to send reconciliation email. Please try again.' });
        } finally {
            setReconciliationSending(false);
        }
    }, [forceResend, reconcileDate, store?.id]);

    return {
        auditDateRange,
        setAuditDateRange,
        auditReports,
        auditReportsError,
        auditReportsLoading,
        clearReconciliationResponse,
        closeReconciliationDialog,
        dateRangeLabel,
        forceResend,
        handleSendReconciliationEmail,
        loadAuditReports,
        openReconciliationDialog,
        reconcileDate,
        reconcileDialogOpen,
        reconciliationResponse,
        reconciliationSending,
        setForceResend,
        setReconcileDate,
    };
};
